import { flowRight } from 'lodash';
import React, { useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { CheckBoxGroupWithNoneOfTheseApply } from '@zocdoc-frontend-common/components-checkboxgroup';
import { ErrorMessage } from '@zocdoc-frontend-common/components-errormessage';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
    withCustomMetrics,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { red80, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import { labelSmall } from '@zocdoc-mezzanine/foundations-typography';
import { REVIEW_AND_BOOK_ROOT_KEY } from '../../constants';
import { clearMentalHealthMissingError } from '../../pages/ReviewAndBook/actions';
import bookingStateSelector from '../../selectors/BookingStateSelector';
import { useObserver } from '../../utils/hooks/useObserver';
import { setMentalHealthRequirementValue } from './actions';
import { SUGGESTED_MSG_KEY } from './constants';
import { getMentalHealthRequirement } from './utils';

export const MentalHealthQuestionnaire = ({
    options,
    dispatchUpdateMentalHealthRequirement = () => {},
    showError = false,
    dispatchClearErrorMessage = () => {},
    sendMetric = () => {},
}) => {
    const observed = useRef(null);
    const handleChange = useCallback(
        (selectedCheckboxes) => {
            dispatchClearErrorMessage();
            dispatchUpdateMentalHealthRequirement(selectedCheckboxes);
        },
        [dispatchClearErrorMessage, dispatchUpdateMentalHealthRequirement]
    );

    const callback = useCallback(
        () =>
            sendMetric({
                element: 'MentalHealthQuestionnaire',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                action: 'viewed mental health questionnaire',
            }),
        [sendMetric]
    );

    useObserver(observed, { onlyOnce: true, threshold: 0.1 }, callback);

    return (
        <div data-test="mental-health-questionnaire" ref={observed}>
            <Label data-test="mental-health-label">
                Do any of these apply to the patient’s reason for the visit?
                Select all that apply
            </Label>
            <CheckboxGroupWrapper showError={showError}>
                <CheckBoxGroupWithNoneOfTheseApply
                    options={options}
                    onChange={handleChange}
                />
            </CheckboxGroupWrapper>

            <Disclaimer data-test="mental-health-disclaimer">
                Required by your provider
            </Disclaimer>
            {showError && (
                <ErrorMessage data-test="mental-health-error-message">
                    Please make a selection to continue
                </ErrorMessage>
            )}
        </div>
    );
};

const CheckboxGroupWrapper = styled.div`
    border: ${(props) =>
        props.showError ? `1px solid ${red80}` : `1px solid ${slate30}`};
`;

const Label = styled.div`
    ${labelSmall}
    padding-bottom: 7px;
`;
const Disclaimer = styled.div`
    padding-top: 4px;
    font: 13px/24px ${FontFamily.SharpSansMedium};
`;

export const mapStateToProps = (state) => {
    const bookingState = bookingStateSelector(state);
    const showError =
        state?.[REVIEW_AND_BOOK_ROOT_KEY]
            ?.showLifestanceMentalHealthMissingError;
    const requirement = getMentalHealthRequirement(bookingState);
    let options = [];

    const suggestedMsg = JSON.parse(requirement?.[SUGGESTED_MSG_KEY]);

    Object.keys(suggestedMsg).forEach((key) => {
        options = [...options, { label: suggestedMsg[key], value: key }];
    });

    return {
        options,
        showError,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchUpdateMentalHealthRequirement: (value) => {
            dispatch(setMentalHealthRequirementValue(value));
        },
        dispatchClearErrorMessage: () => {
            dispatch(clearMentalHealthMissingError());
        },
    };
};

export default flowRight(
    connect(mapStateToProps, mapDispatchToProps),
    withCustomMetrics({})
)(MentalHealthQuestionnaire);
